import type { ApplicationSessionRefreshOut } from "@/api-client/types.gen";

type StoredAccessToken = {
  readonly accessToken: string;
  readonly refreshAt: number;
};

export type TokenResponse = Pick<ApplicationSessionRefreshOut, "access_token" | "expires_in">;

const LOGOUT_PENDING_KEY = "trux-auth-logout-pending";

let storedAccessToken: StoredAccessToken | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

export function monotonicNow(): number {
  return performance.now();
}

export function refreshLeewaySeconds(expiresIn: number): number {
  return Math.min(60, Math.max(5, expiresIn * 0.1));
}

export function saveAccessToken(response: TokenResponse, receivedAt = monotonicNow()) {
  const refreshAfterSeconds = response.expires_in - refreshLeewaySeconds(response.expires_in);
  storedAccessToken = {
    accessToken: response.access_token,
    refreshAt: receivedAt + refreshAfterSeconds * 1000,
  };
  notifyListeners();
}

export function clearAccessToken() {
  if (storedAccessToken === null) {
    return;
  }
  storedAccessToken = null;
  notifyListeners();
}

export function getAccessToken(): string | null {
  return storedAccessToken?.accessToken ?? null;
}

export function msUntilRefresh(now = monotonicNow()): number | null {
  if (storedAccessToken === null) {
    return null;
  }
  return Math.max(0, storedAccessToken.refreshAt - now);
}

export function isRefreshDue(now = monotonicNow()): boolean {
  return storedAccessToken !== null && now >= storedAccessToken.refreshAt;
}

export function subscribeToAccessToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function withStorage<T>(action: () => T): T | undefined {
  try {
    return action();
  } catch {
    return undefined;
  }
}

export function isLogoutPending(): boolean {
  return withStorage(() => localStorage.getItem(LOGOUT_PENDING_KEY) !== null) === true;
}

export function markLogoutPending() {
  withStorage(() => {
    localStorage.setItem(LOGOUT_PENDING_KEY, "1");
  });
}

export function clearLogoutPending() {
  withStorage(() => {
    localStorage.removeItem(LOGOUT_PENDING_KEY);
  });
}
